import axios from 'axios';

const baseApiURL = process.env.REACT_APP_API_BASE_URL;

const LOGIN_PATH = '/login';

/**
 * Encerra a sessão do mesmo jeito que o `signOut` do `hooks/auth`: **navegação
 * dura**, não `history.push`.
 *
 * O `push` deixava um logout pela metade. Ele limpa o `localStorage`, mas o
 * `userAuth` do contexto continua preenchido — e o guarda de rota
 * (`routes/Route.tsx`) decide por esse estado, não pelo token. Resultado: `/login`
 * é rota pública, o guarda via um usuário "logado" e redirecionava de volta para
 * `/dashboard/profile`, que remontava, refazia a requisição com `Bearer null` e
 * tomava 401 de novo — laço infinito com a tela travada.
 *
 * `replace` recarrega a aplicação, então todo estado em memória morre junto. É a
 * única forma de garantir que contexto e storage não fiquem divergentes daqui, que
 * é um módulo fora da árvore do React e não alcança o contexto.
 */
const forceSignOut = (): void => {
  localStorage.removeItem('@Cinco:token');

  // Sem esta guarda o próprio /login entraria em laço caso alguma requisição
  // dele devolvesse 401.
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.replace(
      `${window.location.protocol}//${window.location.host}${LOGIN_PATH}`,
    );
  }
};

const api = axios.create({
  baseURL: baseApiURL,
});

const baseCommunicationServiceURL =
  process.env.REACT_APP_COMMUNICATION_SERVICE_BASE_URL;

const communicationService = axios.create({
  baseURL: baseCommunicationServiceURL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': process.env.REACT_APP_COMMUNICATION_SERVICE_TOKEN ?? '',
  },
});

api.interceptors.request.use(
  async (config) => {
    const userToken = localStorage.getItem('@Cinco:token');
    if (config.headers) {
      // Sem token o header saía como a string literal `Bearer null`, que o back
      // rejeita com 401 — alimentando o mesmo laço. Sem header, o 401 continua
      // vindo, mas por ausência de credencial, que é o que de fato aconteceu.
      if (userToken) {
        config.headers.Authorization = `Bearer ${userToken}`;
      } else {
        delete config.headers.Authorization;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (error) => {
    if (error.response && error.response.status === 401) {
      forceSignOut();
    }
    return Promise.reject(error);
  },
);

/**
 * Instância para a **sessão de paciente** que a ativação devolve (tarefa 0.34), usada só
 * pelo passo de vínculo de WhatsApp da tela de boas-vindas.
 *
 * ⚠️ **Não dá para usar a `api` acima, por duas razões que se somam.** (1) O interceptor de
 * request dela **apaga** o `Authorization` quando não há `@Cinco:token` no `localStorage` —
 * e nesta tela não há, porque quem está do outro lado é o paciente, não a equipe: o header
 * que passássemos por requisição seria removido. (2) O interceptor de response chama
 * `forceSignOut()` em qualquer 401, que faz **navegação dura** para `/login` — um código de
 * vínculo recusado tiraria a pessoa da página no meio da ativação.
 *
 * Sem interceptor nenhum, portanto: o token vai explícito, por requisição, e vive em estado
 * de componente. **Nunca** no `localStorage` — a chave de lá é da sessão da equipe, e
 * misturar as duas é o que criaria confusão de sessão no mesmo navegador.
 */
const patientSessionApi = axios.create({
  baseURL: baseApiURL,
});

export { api, communicationService, patientSessionApi };
